import { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { BasicTable, Button, Notification, PageView, PageTopBar } from '../../../components'
import { Loader } from '../../../components/Loader'
import { fetchWarehouse } from '../../../services/warehouseSevices'
import { fetchReceivingCapacityOverrides, createReceivingCapacityOverride, fetchReceivingFranjas } from '../../../services'
import { todayInTimezone, addDaysToDateString } from '../../../utils/warehouseDates'
import { markSupersededOverrides, translateAppointmentError } from '../../../utils/inboundAppointments'
import { formatDate } from '../../CubboLastmile/utils'
import { boardLocale } from '../../InboundAppointments/FranjaCapacityStrip'
import { ReceivingCapacityOverrideFormModal } from './ReceivingCapacityOverrideFormModal'
import { ReceivingTabs } from '../Receiving/ReceivingTabs'
import { FIVE_MINUTES_MS } from '../../../helpers/cacheTimes'

const T = 'replenishments.inbound_appointments.overrides'

// Append-only capacity-overrides editor (contract §4.5): list + create modal,
// following the Configurations/SalesChannels pattern. No edit/delete in v1 —
// corrections land by appending a newer row (latest row wins).
export const ListReceivingCapacityOverridesContainer = () => {
  const { i18n } = useTranslation()
  const { warehouse_id } = useParams()
  const queryClient = useQueryClient()

  // State holds only explicit user picks; the default horizon (warehouse-local
  // today → +30 days) is DERIVED — never seeded from onSuccess, which does not
  // fire when the warehouse resolves from fresh cache (eng-review D16).
  const [pickedRange, setPickedRange] = useState(null)
  const [formOpen, setFormOpen] = useState(false)
  const [notification, setNotification] = useState({ show: false, type: '', title: '' })
  const [serverError, setServerError] = useState(null)

  const {
    data: warehouse,
    isError: warehouseIsError,
    refetch: refetchWarehouse,
  } = useQuery(['warehouse', warehouse_id], () => fetchWarehouse(warehouse_id), {
    staleTime: FIVE_MINUTES_MS,
    refetchOnWindowFocus: false,
    enabled: !!warehouse_id,
  })

  const range = useMemo(() => {
    if (pickedRange) return pickedRange
    if (!warehouse) return null
    const today = todayInTimezone(warehouse.time_zone)
    return { from: today, to: addDaysToDateString(today, 30) }
  }, [pickedRange, warehouse])

  // Clamp so the API never sees an inverted range (eng-review D27). A null
  // range while the warehouse is still RESOLVING must not block manual entry
  // (final-review D8) — seed both ends from the typed value. (If the warehouse
  // query ERRORS, the render below shows the retry strip regardless; manual
  // entry only pre-seeds the range for after a successful retry.)
  const handleFromChange = value => {
    if (!value) return // a cleared input must never reach the API as from=''
    if (!range) {
      setPickedRange({ from: value, to: value })
      return
    }
    setPickedRange({ from: value, to: value > range.to ? value : range.to })
  }
  const handleToChange = value => {
    if (!value) return
    if (!range) {
      setPickedRange({ from: value, to: value })
      return
    }
    setPickedRange({ from: value < range.from ? value : range.from, to: value })
  }

  const {
    data: franjas,
    isError: franjasIsError,
    refetch: refetchFranjas,
  } = useQuery(['receiving_franjas', warehouse_id], () => fetchReceivingFranjas(warehouse_id), {
    staleTime: FIVE_MINUTES_MS,
    refetchOnWindowFocus: false,
    enabled: !!warehouse_id,
  })

  const {
    data: overrides,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery(['receiving_capacity_overrides', warehouse_id, range], () => fetchReceivingCapacityOverrides(warehouse_id, { from: range.from, to: range.to }), {
    enabled: !!warehouse_id && !!range,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })

  const createMutation = useMutation(payload => createReceivingCapacityOverride(warehouse_id, payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(['receiving_capacity_overrides', warehouse_id])
      queryClient.invalidateQueries(['inbound_franja_summary', warehouse_id])
      setFormOpen(false)
      setServerError(null)
      setNotification({ show: true, type: 'success', title: i18n.t(`${T}.notifications.created`) })
    },
    onError: error => setServerError(translateAppointmentError(i18n, error)),
  })

  // useCallback so the tableData useMemo can list it honestly — CI's lint job
  // is an exhaustive-deps warning ratchet (eng-review D20).
  const franjaLabel = useCallback(
    franjaId => {
      if (!franjaId) return i18n.t(`${T}.all_day`)
      const franja = (franjas || []).find(f => f.id === franjaId)
      return franja ? `${franja.label} · ${franja.starts_at} – ${franja.ends_at}` : `#${franjaId}`
    },
    [franjas, i18n],
  )

  const columns = useMemo(
    () => [
      { Header: i18n.t(`${T}.headers.date`), accessor: 'date', disableSortBy: true },
      { Header: i18n.t(`${T}.headers.franja`), accessor: 'franja', disableSortBy: true },
      { Header: i18n.t(`${T}.headers.dock`), accessor: 'dock', disableSortBy: true },
      { Header: i18n.t(`${T}.headers.man_hours`), accessor: 'man_hours', disableSortBy: true },
      { Header: i18n.t(`${T}.headers.units_delta`), accessor: 'units_delta', disableSortBy: true },
      { Header: i18n.t(`${T}.headers.blocked`), accessor: 'blocked', disableSortBy: true },
      { Header: i18n.t(`${T}.headers.reason`), accessor: 'reason', disableSortBy: true },
      { Header: i18n.t(`${T}.headers.created_at`), accessor: 'created_at', disableSortBy: true },
    ],
    [i18n],
  )

  // Backend-faithful supersede marking (per-lever, blocked-sticky — Task 9):
  // superseded rows stay listed but dimmed; blocked rows are never dimmed.
  const tableData = useMemo(
    () =>
      markSupersededOverrides(overrides || []).map(row => {
        const dim = content => <span className={row.isSuperseded ? 'opacity-40' : ''}>{content}</span>
        return {
          date: (
            <span className={row.isSuperseded ? 'opacity-40' : ''}>
              {row.date}
              {row.isSuperseded && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                  {i18n.t(`${T}.superseded`)}
                </span>
              )}
            </span>
          ),
          franja: dim(franjaLabel(row.receiving_franja_id)),
          dock: dim(row.dock_count_override ?? '—'),
          man_hours: dim(row.man_hours_override ?? '—'),
          units_delta: dim(row.units_delta == null ? '—' : `${row.units_delta > 0 ? '+' : ''}${row.units_delta.toLocaleString(boardLocale(i18n))}`),
          blocked: row.blocked ? (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 ${row.isSuperseded ? 'opacity-40' : ''}`}
            >
              {i18n.t(`${T}.blocked_pill`)}
            </span>
          ) : (
            dim('—')
          ),
          reason: dim(row.reason || '—'),
          created_at: dim(formatDate(row.created_at, i18n.language, warehouse?.time_zone)),
        }
      }),
    [overrides, franjaLabel, i18n, warehouse],
  )

  const inputClasses = 'border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500'

  return (
    <PageView
      topMenu={
        <PageTopBar>
          <div className="text-lg font-semibold">{i18n.t(`${T}.title`)}</div>
          <div className="flex items-center gap-2 ml-auto">
            <input
              type="date"
              className={inputClasses}
              value={range?.from || ''}
              onChange={e => handleFromChange(e.target.value)}
              aria-label={i18n.t(`${T}.from`)}
            />
            <input
              type="date"
              className={inputClasses}
              value={range?.to || ''}
              min={range?.from}
              onChange={e => handleToChange(e.target.value)}
              aria-label={i18n.t(`${T}.to`)}
            />
            <Button
              type="primary"
              onClick={() => {
                setServerError(null)
                setFormOpen(true)
              }}
            >
              {i18n.t(`${T}.create`)}
            </Button>
          </div>
        </PageTopBar>
      }
      childrenFullWidth={true}
      topMenuFullWidth={true}
    >
      <ReceivingTabs />
      <Notification
        show={notification.show}
        setShow={val => setNotification(prev => ({ ...prev, show: val }))}
        type={notification.type}
        title={notification.title}
      />

      {warehouseIsError ? (
        // The default range derives from the warehouse query — its failure
        // must never strand the page on a permanent spinner (final-review D8).
        <div className="text-center py-12">
          <p className="text-red-600 font-medium mb-3">{i18n.t(`${T}.load_error`)}</p>
          <Button type="secondary" onClick={() => refetchWarehouse()}>
            {i18n.t(`${T}.retry`)}
          </Button>
        </div>
      ) : franjasIsError ? (
        // Franja labels + the create modal's scope options come from the
        // catalog; a failed fetch silently degrading every row to "todo el
        // día" would misrepresent the data (4A).
        <div className="text-center py-12">
          <p className="text-red-600 font-medium mb-3">{i18n.t(`${T}.franjas_load_error`)}</p>
          <Button type="secondary" onClick={() => refetchFranjas()}>
            {i18n.t(`${T}.retry`)}
          </Button>
        </div>
      ) : isLoading || !range ? (
        <Loader show={true} />
      ) : isError ? (
        // A broken list must never impersonate an empty range (eng-review 4A).
        <div className="text-center py-12">
          <p className="text-red-600 font-medium mb-3">{i18n.t(`${T}.load_error`)}</p>
          <Button type="secondary" onClick={() => refetch()}>
            {i18n.t(`${T}.retry`)}
          </Button>
        </div>
      ) : (
        <BasicTable
          columns={columns}
          data={tableData}
          showHeader={true}
          showLoader={isFetching}
          isFetching={isFetching}
          emptyTableText={i18n.t(`${T}.no_overrides`)}
          showPaginator={false}
        />
      )}

      <ReceivingCapacityOverrideFormModal
        open={formOpen}
        setOpen={setFormOpen}
        franjas={franjas || []}
        warehouseId={warehouse_id}
        onSubmit={payload => createMutation.mutate(payload)}
        isLoading={createMutation.isLoading}
        serverError={serverError}
      />
    </PageView>
  )
}
